import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import { SyntheticEvent } from "react";
import { useNavigate } from "react-router-dom";

import { useQueryParam } from "@/hooks/useQueryParam";
import { clearContext } from "@/provider/DataContextProvider";
import { useLogout as useApiLogout } from "@/services/auth";
import {
  accessTokenAtom,
  addDeviceFormDataAtom,
  AlertTypes,
  refreshTokenAtom,
  showsSessionExpiredModalAtom,
  verifierAtom,
} from "@/utils/atoms";

import { useAlert } from "./useAlert";

export const useLogout = () => {
  const setAccessToken = useSetAtom(accessTokenAtom);
  const setRefreshToken = useSetAtom(refreshTokenAtom);
  const setShowSessionExpiredModal = useSetAtom(showsSessionExpiredModalAtom);
  const setAddDeviceFormData = useSetAtom(addDeviceFormDataAtom);
  const showAlert = useAlert();

  const setVerifier = useSetAtom(verifierAtom);
  const [, setCode] = useQueryParam("code");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clearAppContext = () => {
    clearContext();
    setCode("");
    queryClient.clear();
    setAccessToken(null);
    setRefreshToken(null);
    setVerifier(undefined);
    setShowSessionExpiredModal(undefined);
    setAddDeviceFormData(null);
    localStorage.clear();
    // Remove info from sessionStorage to show attention bubble again after login
    sessionStorage.removeItem("showFriendsWithBenefitsHint");
    navigate("/welcome");
  };
  const { mutate } = useApiLogout({
    mutation: {
      onSuccess: () => {
        clearAppContext();
      },
      onError: () => {
        clearAppContext();
      },
    },
  });

  // As this is commonly wired straight into button onClicks, React might call it with the SyntheticEvent...
  const logout = (emailVerificationErrorOrEvent?: string | SyntheticEvent) => {
    // ...so we need to avoid an event triggering an error alert
    const emailVerificationError =
      typeof emailVerificationErrorOrEvent === "string"
        ? emailVerificationErrorOrEvent
        : undefined;

    if (emailVerificationError) {
      showAlert({
        type: AlertTypes.error,
        text: emailVerificationError,
      });
    }
    mutate();
  };

  return logout;
};
